import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, Button, Grid, CircularProgress, Alert,
    Select, MenuItem, InputLabel, FormControl, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, Divider, Chip, FormHelperText, useTheme, alpha, Paper,
    FormControlLabel, Checkbox, Stepper, Step, StepLabel
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import {
    PointOfSale as CategoryIcon,
    LinearScale as TypeIcon,
    ViewInAr as SubtypeIcon,
    Place as LocationIcon,
    Route as NetworkIcon,
    Timeline as SectionIcon,
    SettingsEthernet as ChainageIcon,
    Label as CodeIcon,
    Description as DescriptionIcon,
    CalendarMonth as DateIcon,
    Public as GeometryIcon,
    Close as CloseIcon,
    FileUpload as FileUploadIcon,
    Map as MapIcon,
    Warning as WarningIcon,
    CellTower as HighMastIcon,
    Check as CheckIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import {
    organizationsApi, networksApi, sectionsApi, chainageApi,
    classificationApi, assetsApi
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import wellknown from 'wellknown';
import { kml } from '@mapbox/togeojson';
import GeometryEditorModal from './controls/GeometryEditorModal';

const CreateAssetForm = ({ onSubmit, onCancel }) => {
    const theme = useTheme();
    const { user } = useAuth();
    const [isMapModalOpen, setMapModalOpen] = useState(false);

    // Check if user can set critical flag (Admin/Manager only)
    const canSetCritical = user && ['admin', 'manager'].includes(user.role);

    // Stepper State
    const [activeStep, setActiveStep] = useState(0);
    const [errors, setErrors] = useState({});

    const steps = [
        { label: 'Classification', icon: <CategoryIcon /> },
        { label: 'Location', icon: <LocationIcon /> },
        { label: 'Details', icon: <DescriptionIcon /> },
        { label: 'Review', icon: <CheckIcon /> },
    ];

    // Form Data State
    const [formData, setFormData] = useState({
        organization_id: '',
        network_id: '',
        section_id: '',
        chainage_segment_id: '',
        localized_chainage_segment_id: '',
        category_id: '',
        type_id: '',
        subtype_id: '',
        name: '',
        code: '',
        description: '',
        status: 'active',
        install_date: '',
        geometry: '',
        is_critical: false,
        metadata: {},
    });

    // Dropdown Options State
    const [organizations, setOrganizations] = useState([]);
    const [networks, setNetworks] = useState([]);
    const [sections, setSections] = useState([]);
    const [chainageSegments, setChainageSegments] = useState([]);
    const [localizedSegments, setLocalizedSegments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [types, setTypes] = useState([]);
    const [subtypes, setSubtypes] = useState([]);

    // Geometry Input State
    const [geometryInput, setGeometryInput] = useState('');
    const [geometryError, setGeometryError] = useState(null);
    const [expectedGeometryType, setExpectedGeometryType] = useState(null);
    const [geometrySource, setGeometrySource] = useState(null);

    // Loading/Error State
    const [loading, setLoading] = useState({
        orgs: false, networks: false, sections: false, chainage: false, localized: false,
        cats: false, types: false, subtypes: false, submit: false
    });
    const [error, setError] = useState(null);

    // --- Data Fetching Effects ---

    useEffect(() => {
        const fetchInitial = async () => {
            setLoading(prev => ({ ...prev, orgs: true, cats: true }));
            try {
                const [orgRes, catRes] = await Promise.all([
                    organizationsApi.getAll(),
                    classificationApi.getCategories()
                ]);
                setOrganizations(orgRes.data);
                setCategories(catRes.data);
            } catch (err) {
                console.error("Error fetching initial dropdown data:", err);
                setError("Failed to load initial form data.");
            } finally {
                setLoading(prev => ({ ...prev, orgs: false, cats: false }));
            }
        };
        fetchInitial();
    }, []);

    useEffect(() => {
        if (!formData.organization_id) {
            setNetworks([]);
            setFormData(prev => ({ ...prev, network_id: '', section_id: '', chainage_segment_id: '', localized_chainage_segment_id: '' }));
            return;
        }
        const fetchNetworks = async () => {
            setLoading(prev => ({ ...prev, networks: true }));
            try {
                const response = await networksApi.getAll({ organization_id: formData.organization_id });
                setNetworks(response.data);
            } catch (err) {
                console.error("Error fetching networks:", err);
            } finally {
                setLoading(prev => ({ ...prev, networks: false }));
            }
        };
        fetchNetworks();
    }, [formData.organization_id]);

    useEffect(() => {
        if (!formData.network_id) {
            setSections([]);
            setFormData(prev => ({ ...prev, section_id: '', chainage_segment_id: '', localized_chainage_segment_id: '' }));
            return;
        }
        const fetchSections = async () => {
            setLoading(prev => ({ ...prev, sections: true }));
            try {
                const response = await sectionsApi.getAll({ network_id: formData.network_id });
                setSections(response.data);
            } catch (err) {
                console.error("Error fetching sections:", err);
            } finally {
                setLoading(prev => ({ ...prev, sections: false }));
            }
        };
        fetchSections();
    }, [formData.network_id]);

    useEffect(() => {
        if (!formData.section_id) {
            setChainageSegments([]);
            setFormData(prev => ({ ...prev, chainage_segment_id: '', localized_chainage_segment_id: '' }));
            return;
        }
        const fetchChainage = async () => {
            setLoading(prev => ({ ...prev, chainage: true }));
            try {
                const response = await chainageApi.getChainageSegments({ section_id: formData.section_id });
                setChainageSegments(response.data);
            } catch (err) {
                console.error("Error fetching chainage segments:", err);
            } finally {
                setLoading(prev => ({ ...prev, chainage: false }));
            }
        };
        fetchChainage();
    }, [formData.section_id]);

    useEffect(() => {
        if (!formData.chainage_segment_id) {
            setLocalizedSegments([]);
            setFormData(prev => ({ ...prev, localized_chainage_segment_id: '' }));
            return;
        }
        const fetchLocalized = async () => {
            setLoading(prev => ({ ...prev, localized: true }));
            try {
                const response = await chainageApi.getLocalizedChainageSegments({ chainage_segment_id: formData.chainage_segment_id });
                setLocalizedSegments(response.data);
            } catch (err) {
                console.error("Error fetching localized segments:", err);
            } finally {
                setLoading(prev => ({ ...prev, localized: false }));
            }
        };
        fetchLocalized();
    }, [formData.chainage_segment_id]);

    useEffect(() => {
        if (!formData.category_id) {
            setTypes([]);
            setFormData(prev => ({ ...prev, type_id: '', subtype_id: '' }));
            return;
        }
        const fetchTypes = async () => {
            setLoading(prev => ({ ...prev, types: true }));
            try {
                const response = await classificationApi.getTypes({ category_id: formData.category_id });
                setTypes(response.data);
            } catch (err) {
                console.error("Error fetching asset types:", err);
            } finally {
                setLoading(prev => ({ ...prev, types: false }));
            }
        };
        fetchTypes();
    }, [formData.category_id]);

    useEffect(() => {
        if (!formData.type_id) {
            setSubtypes([]);
            setFormData(prev => ({ ...prev, subtype_id: '' }));
            setExpectedGeometryType(null);
            return;
        }
        const selectedType = types.find(type => type.id === formData.type_id);
        if (selectedType) {
            setExpectedGeometryType(selectedType.geometry_type);
        } else {
            setExpectedGeometryType(null);
        }
        const fetchSubtypes = async () => {
            setLoading(prev => ({ ...prev, subtypes: true }));
            try {
                const response = await classificationApi.getSubtypes({ type_id: formData.type_id });
                setSubtypes(response.data);
            } catch (err) {
                console.error("Error fetching asset subtypes:", err);
            } finally {
                setLoading(prev => ({ ...prev, subtypes: false }));
            }
        };
        fetchSubtypes();
    }, [formData.type_id, types]);

    // --- Derived state ---
    const selectedType = types.find(t => t.id === formData.type_id);
    const isHighMast = selectedType?.name === 'High Mast';
    const requiresChainage = selectedType?.requires_chainage_segment !== false;

    // --- Lookup helpers for review step ---
    const findName = (list, id) => list.find(item => item.id === id)?.name || '';

    // --- Handlers ---
    const handleMetadataChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            metadata: { ...prev.metadata, [field]: value }
        }));
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError(null);
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    // --- Step Validation ---
    const validateStep = (step) => {
        const newErrors = {};
        switch (step) {
            case 0:
                if (!formData.category_id) newErrors.category_id = 'Category is required';
                if (!formData.type_id) newErrors.type_id = 'Type is required';
                if (!formData.subtype_id) newErrors.subtype_id = 'Subtype is required';
                break;
            case 1:
                if (!formData.organization_id) newErrors.organization_id = 'Organization is required';
                if (!formData.network_id) newErrors.network_id = 'Network is required';
                if (!formData.section_id) newErrors.section_id = 'Section is required';
                break;
            case 2:
                if (!formData.name?.trim()) newErrors.name = 'Asset name is required';
                if (!formData.code?.trim()) newErrors.code = 'Asset code is required';
                if (!formData.geometry) newErrors.geometry = 'Geometry is required';
                break;
            default:
                break;
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    // --- Geometry Handlers ---
    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length === 0) {
            setGeometryError("No file selected or file type not accepted.");
            setFormData(prev => ({ ...prev, geometry: '' }));
            setGeometryInput('');
            return;
        }
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onabort = () => { setGeometryError('File reading was aborted'); setFormData(prev => ({ ...prev, geometry: '' })); setGeometryInput(''); };
        reader.onerror = () => { setGeometryError('File reading has failed'); setFormData(prev => ({ ...prev, geometry: '' })); setGeometryInput(''); };
        reader.onload = () => { setGeometryInput(reader.result); processGeometryInput(reader.result); };
        reader.readAsText(file);
    }, [expectedGeometryType, formData.type_id]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/vnd.google-earth.kml+xml': ['.kml'], 'application/geo+json': ['.geojson', '.json'] },
        multiple: false
    });

    const processGeometryInput = (inputString) => {
        setGeometryError(null);
        let wktGeometry = '';
        let geometryType = null;
        let parsedSuccessfully = false;

        const stripZ = (coords) => {
            if (Array.isArray(coords) && coords.length > 0) {
                if (typeof coords[0] === 'number' && coords.length > 2) return coords.slice(0, 2);
                return coords.map(stripZ);
            }
            return coords;
        };

        // Try GeoJSON
        try {
            const geojson = JSON.parse(inputString);
            if (geojson && geojson.type) {
                let featureGeometry = geojson;
                if (geojson.type === 'FeatureCollection' && geojson.features?.length > 0) featureGeometry = geojson.features[0].geometry;
                else if (geojson.type === 'Feature' && geojson.geometry) featureGeometry = geojson.geometry;

                if (featureGeometry?.type && featureGeometry?.coordinates) {
                    const geom2d = JSON.parse(JSON.stringify(featureGeometry));
                    if (geom2d.coordinates) geom2d.coordinates = stripZ(geom2d.coordinates);
                    wktGeometry = wellknown.stringify(geom2d);
                    geometryType = featureGeometry.type.toLowerCase();
                    parsedSuccessfully = true;
                }
            }
        } catch (e) { /* not JSON, try KML */ }

        // Try KML
        if (!parsedSuccessfully) {
            try {
                const parser = new DOMParser();
                const kmlDoc = parser.parseFromString(inputString, "text/xml");
                if (kmlDoc.getElementsByTagName("parsererror").length > 0) throw new Error("Invalid KML");
                const geojsonFromKml = kml(kmlDoc);
                if (geojsonFromKml?.features?.length > 0) {
                    const featureGeometry = geojsonFromKml.features[0].geometry;
                    if (featureGeometry?.type && featureGeometry?.coordinates) {
                        const geom2d = JSON.parse(JSON.stringify(featureGeometry));
                        if (geom2d.coordinates) geom2d.coordinates = stripZ(geom2d.coordinates);
                        wktGeometry = wellknown.stringify(geom2d);
                        geometryType = featureGeometry.type.toLowerCase();
                        parsedSuccessfully = true;
                    }
                }
            } catch (kmlError) {
                setGeometryError(`Error processing file: ${kmlError.message}`);
                setFormData(prev => ({ ...prev, geometry: '' }));
                return;
            }
        }

        if (!parsedSuccessfully) {
            setGeometryError("Could not parse input as valid GeoJSON or KML.");
            setFormData(prev => ({ ...prev, geometry: '' }));
            return;
        }

        if (expectedGeometryType && geometryType !== expectedGeometryType.toLowerCase()) {
            setGeometryError(`Geometry type mismatch. Expected: ${expectedGeometryType}, Received: ${geometryType}`);
            setFormData(prev => ({ ...prev, geometry: '' }));
        } else {
            setFormData(prev => ({ ...prev, geometry: wktGeometry }));
            setGeometrySource('file');
            if (errors.geometry) setErrors(prev => ({ ...prev, geometry: null }));
        }
    };

    const handleSaveGeometry = (geoJson) => {
        if (geoJson) {
            try {
                setGeometryError(null);
                const featureGeometry = geoJson.geometry || geoJson;
                const stripZ = (coords) => {
                    if (Array.isArray(coords) && coords.length > 0) {
                        if (typeof coords[0] === 'number' && coords.length > 2) return coords.slice(0, 2);
                        return coords.map(stripZ);
                    }
                    return coords;
                };
                const geom2d = JSON.parse(JSON.stringify(featureGeometry));
                if (geom2d.coordinates) geom2d.coordinates = stripZ(geom2d.coordinates);
                const wktGeometry = wellknown.stringify(geom2d);
                const geometryType = featureGeometry.type.toLowerCase();
                if (expectedGeometryType && geometryType !== expectedGeometryType.toLowerCase()) {
                    setGeometryError(`Geometry type mismatch. Expected: ${expectedGeometryType}, Received: ${geometryType}`);
                    setFormData(prev => ({ ...prev, geometry: '' }));
                } else {
                    setFormData(prev => ({ ...prev, geometry: wktGeometry }));
                    setGeometrySource('draw');
                    if (errors.geometry) setErrors(prev => ({ ...prev, geometry: null }));
                }
            } catch (err) {
                setGeometryError("Failed to process the drawn geometry.");
            }
        }
        setMapModalOpen(false);
    };

    // --- Submit ---
    const handleSubmit = async () => {
        setLoading(prev => ({ ...prev, submit: true }));
        setError(null);
        try {
            const dataToSend = { ...formData };
            if (!dataToSend.chainage_segment_id) delete dataToSend.chainage_segment_id;
            if (!dataToSend.localized_chainage_segment_id) delete dataToSend.localized_chainage_segment_id;
            if (!dataToSend.install_date) delete dataToSend.install_date;
            if (!dataToSend.description) delete dataToSend.description;
            delete dataToSend.category_id;
            delete dataToSend.type_id;
            const hasMetadata = Object.values(dataToSend.metadata || {}).some(v => v !== '' && v != null);
            if (!hasMetadata) delete dataToSend.metadata;

            await assetsApi.create(dataToSend);
            onSubmit(dataToSend);
        } catch (err) {
            console.error("Error creating asset:", err);
            const errorMessage = err.response?.data?.error || err.message || "Failed to create asset.";
            setError(errorMessage);
            onSubmit(formData, { error: errorMessage });
        } finally {
            setLoading(prev => ({ ...prev, submit: false }));
        }
    };

    // --- Render Helpers ---
    const renderOptions = (items) => items.map(item => (
        <MenuItem key={item.id} value={item.id}>
            {item.name} {item.code ? <Chip size="small" label={item.code} sx={{ ml: 1 }} /> : ''}
        </MenuItem>
    ));

    const selectMenuProps = { PaperProps: { style: { maxHeight: 300 } } };

    const renderSelectValue = (list) => (selected) => {
        const item = list.find(i => i.id === selected);
        return item ? <Typography noWrap>{item.name}</Typography> : '';
    };

    // --- Review Row Helper ---
    const ReviewRow = ({ label, value, chip }) => (
        value ? (
            <Box sx={{ display: 'flex', py: 0.75, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}` }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 160, flexShrink: 0 }}>{label}</Typography>
                {chip ? (
                    <Chip label={value} size="small" sx={{ fontSize: '0.8rem' }} />
                ) : (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
                )}
            </Box>
        ) : null
    );

    // =====================================================
    // STEP CONTENT RENDERER
    // =====================================================
    const renderStepContent = (step) => {
        switch (step) {
            // ----- STEP 0: Classification -----
            case 0:
                return (
                    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                            Select asset classification
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Choose the category, type, and subtype for the new asset.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <FormControl fullWidth required disabled={loading.cats} error={!!errors.category_id}>
                                <InputLabel>Category</InputLabel>
                                <Select name="category_id" value={formData.category_id} label="Category" onChange={handleChange} MenuProps={selectMenuProps} renderValue={renderSelectValue(categories)}>
                                    {renderOptions(categories)}
                                </Select>
                                {errors.category_id && <FormHelperText>{errors.category_id}</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth required disabled={!formData.category_id || loading.types} error={!!errors.type_id}>
                                <InputLabel>Type</InputLabel>
                                <Select name="type_id" value={formData.type_id} label="Type" onChange={handleChange} MenuProps={selectMenuProps} renderValue={renderSelectValue(types)}>
                                    {renderOptions(types)}
                                </Select>
                                {errors.type_id && <FormHelperText>{errors.type_id}</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth required disabled={!formData.type_id || loading.subtypes} error={!!errors.subtype_id}>
                                <InputLabel>Subtype</InputLabel>
                                <Select name="subtype_id" value={formData.subtype_id} label="Subtype" onChange={handleChange} MenuProps={selectMenuProps} renderValue={renderSelectValue(subtypes)}>
                                    {renderOptions(subtypes)}
                                </Select>
                                {errors.subtype_id && <FormHelperText>{errors.subtype_id}</FormHelperText>}
                            </FormControl>
                        </Box>
                        {expectedGeometryType && (
                            <Alert severity="info" sx={{ mt: 3 }}>
                                Expected geometry type: <strong>{expectedGeometryType}</strong>
                            </Alert>
                        )}
                    </Box>
                );

            // ----- STEP 1: Location -----
            case 1:
                return (
                    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                            Set asset location
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Select the organizational and spatial hierarchy for this asset.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <FormControl fullWidth required disabled={loading.orgs} error={!!errors.organization_id}>
                                <InputLabel>Organization</InputLabel>
                                <Select name="organization_id" value={formData.organization_id} label="Organization" onChange={handleChange} MenuProps={selectMenuProps} renderValue={renderSelectValue(organizations)}>
                                    {renderOptions(organizations)}
                                </Select>
                                {errors.organization_id && <FormHelperText>{errors.organization_id}</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth required disabled={!formData.organization_id || loading.networks} error={!!errors.network_id}>
                                <InputLabel>Network</InputLabel>
                                <Select name="network_id" value={formData.network_id} label="Network" onChange={handleChange} MenuProps={selectMenuProps} renderValue={renderSelectValue(networks)}>
                                    {renderOptions(networks)}
                                </Select>
                                {errors.network_id && <FormHelperText>{errors.network_id}</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth required disabled={!formData.network_id || loading.sections} error={!!errors.section_id}>
                                <InputLabel>Section</InputLabel>
                                <Select name="section_id" value={formData.section_id} label="Section" onChange={handleChange} MenuProps={selectMenuProps} renderValue={renderSelectValue(sections)}>
                                    {renderOptions(sections)}
                                </Select>
                                {errors.section_id && <FormHelperText>{errors.section_id}</FormHelperText>}
                            </FormControl>
                            {requiresChainage && (
                                <FormControl fullWidth disabled={!formData.section_id || loading.chainage}>
                                    <InputLabel>Chainage Segment</InputLabel>
                                    <Select name="chainage_segment_id" value={formData.chainage_segment_id} label="Chainage Segment" onChange={handleChange} MenuProps={selectMenuProps} renderValue={renderSelectValue(chainageSegments)}>
                                        {renderOptions(chainageSegments)}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>
                    </Box>
                );

            // ----- STEP 2: Details & Geometry -----
            case 2:
                return (
                    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                            Asset details & geometry
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Enter asset information and provide geometry data.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField fullWidth required name="name" label="Asset Name" value={formData.name} onChange={handleChange}
                                    error={!!errors.name} helperText={errors.name} />
                                <TextField fullWidth required name="code" label="Asset Code" value={formData.code} onChange={handleChange}
                                    error={!!errors.code} helperText={errors.code} />
                            </Box>
                            <TextField fullWidth name="description" label="Description" value={formData.description} onChange={handleChange} multiline rows={2} />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select name="status" value={formData.status} label="Status" onChange={handleChange}>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                        <MenuItem value="maintenance">Maintenance</MenuItem>
                                        <MenuItem value="decommissioned">Decommissioned</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField fullWidth name="install_date" label="Install Date" type="date" value={formData.install_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                            </Box>
                            {canSetCritical && (
                                <FormControlLabel
                                    control={
                                        <Checkbox name="is_critical" checked={formData.is_critical}
                                            onChange={(e) => setFormData(prev => ({ ...prev, is_critical: e.target.checked }))}
                                            sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <WarningIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Mark as Critical Infrastructure</Typography>
                                        </Box>
                                    }
                                />
                            )}
                        </Box>

                        {/* High Mast Specific Attributes */}
                        {isHighMast && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <HighMastIcon sx={{ color: '#7c4dff', fontSize: 20 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>High Mast Attributes</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField fullWidth label="Height (meters)" type="number" value={formData.metadata.height || ''}
                                            onChange={(e) => handleMetadataChange('height', e.target.value ? Number(e.target.value) : '')} inputProps={{ min: 0, step: 0.1 }} />
                                        <TextField fullWidth label="Ring Count" type="number" value={formData.metadata.ring_count || ''}
                                            onChange={(e) => handleMetadataChange('ring_count', e.target.value ? Number(e.target.value) : '')} inputProps={{ min: 0, step: 1 }} />
                                        <TextField fullWidth label="Luminaire Count" type="number" value={formData.metadata.luminaire_count || ''}
                                            onChange={(e) => handleMetadataChange('luminaire_count', e.target.value ? Number(e.target.value) : '')} inputProps={{ min: 0, step: 1 }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <FormControl fullWidth>
                                            <InputLabel>Pole Material</InputLabel>
                                            <Select value={formData.metadata.pole_material || ''} label="Pole Material"
                                                onChange={(e) => handleMetadataChange('pole_material', e.target.value)}>
                                                <MenuItem value="Galvanized Steel">Galvanized Steel</MenuItem>
                                                <MenuItem value="Stainless Steel">Stainless Steel</MenuItem>
                                                <MenuItem value="Aluminum">Aluminum</MenuItem>
                                                <MenuItem value="Concrete">Concrete</MenuItem>
                                                <MenuItem value="Other">Other</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField fullWidth label="Manufacturer" value={formData.metadata.manufacturer || ''}
                                            onChange={(e) => handleMetadataChange('manufacturer', e.target.value)} />
                                        <TextField fullWidth label="Model" value={formData.metadata.model || ''}
                                            onChange={(e) => handleMetadataChange('model', e.target.value)} />
                                    </Box>
                                </Box>
                            </>
                        )}

                        {/* Geometry */}
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>Geometry *</Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                                <FormControl fullWidth error={!!geometryError || !!errors.geometry}>
                                    <Paper variant="outlined" {...getRootProps()} sx={{ p: 2.5, textAlign: 'center', cursor: 'pointer', borderColor: isDragActive ? 'primary.main' : 'divider', borderStyle: 'dashed', backgroundColor: isDragActive ? 'action.hover' : 'transparent' }}>
                                        <input {...getInputProps()} />
                                        <FileUploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        <Typography variant="body2" component="span">Drag & drop file, or click to select</Typography>
                                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                            Accepted formats: .kml, .geojson, .json
                                        </Typography>
                                    </Paper>
                                    {geometryError && <FormHelperText>{geometryError}</FormHelperText>}
                                    {errors.geometry && !geometryError && <FormHelperText>{errors.geometry}</FormHelperText>}
                                </FormControl>
                            </Box>
                            <Typography color="text.secondary" sx={{ px: 1 }}>OR</Typography>
                            <Button variant="outlined" startIcon={<MapIcon />} onClick={() => setMapModalOpen(true)} disabled={!expectedGeometryType}
                                sx={{ minWidth: 140, height: 56 }}>
                                Draw on Map
                            </Button>
                        </Box>
                        {geometrySource && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                                Geometry captured from {geometrySource === 'file' ? 'uploaded file' : 'map drawing'}.
                            </Alert>
                        )}
                    </Box>
                );

            // ----- STEP 3: Review -----
            case 3:
                return (
                    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                            Review & confirm
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Please review the details below before creating the asset.
                        </Typography>

                        {/* Classification */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>Classification</Typography>
                            <ReviewRow label="Category" value={findName(categories, formData.category_id)} />
                            <ReviewRow label="Type" value={findName(types, formData.type_id)} />
                            <ReviewRow label="Subtype" value={findName(subtypes, formData.subtype_id)} />
                        </Paper>

                        {/* Location */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>Location</Typography>
                            <ReviewRow label="Organization" value={findName(organizations, formData.organization_id)} />
                            <ReviewRow label="Network" value={findName(networks, formData.network_id)} />
                            <ReviewRow label="Section" value={findName(sections, formData.section_id)} />
                            {formData.chainage_segment_id && <ReviewRow label="Chainage" value={findName(chainageSegments, formData.chainage_segment_id)} />}
                        </Paper>

                        {/* Details */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>Details</Typography>
                            <ReviewRow label="Name" value={formData.name} />
                            <ReviewRow label="Code" value={formData.code} />
                            {formData.description && <ReviewRow label="Description" value={formData.description} />}
                            <ReviewRow label="Status" value={formData.status} chip />
                            {formData.install_date && <ReviewRow label="Install Date" value={formData.install_date} />}
                            {formData.is_critical && <ReviewRow label="Critical" value="Yes" chip />}
                            <ReviewRow label="Geometry" value={geometrySource === 'file' ? 'Uploaded file' : geometrySource === 'draw' ? 'Drawn on map' : 'Not set'} chip />
                        </Paper>

                        {/* High Mast Metadata */}
                        {isHighMast && Object.values(formData.metadata).some(v => v !== '' && v != null) && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7c4dff' }}>High Mast Attributes</Typography>
                                <ReviewRow label="Height" value={formData.metadata.height ? `${formData.metadata.height}m` : null} />
                                <ReviewRow label="Ring Count" value={formData.metadata.ring_count || null} />
                                <ReviewRow label="Luminaire Count" value={formData.metadata.luminaire_count || null} />
                                <ReviewRow label="Pole Material" value={formData.metadata.pole_material || null} />
                                <ReviewRow label="Manufacturer" value={formData.metadata.manufacturer || null} />
                                <ReviewRow label="Model" value={formData.metadata.model || null} />
                            </Paper>
                        )}
                    </Box>
                );

            default:
                return null;
        }
    };

    // =====================================================
    // RENDER
    // =====================================================
    return (
        <React.Fragment>
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                py: 1.5,
                px: 2.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                        Create New Asset
                    </Typography>
                </Box>
                <IconButton onClick={onCancel} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3, minHeight: '50vh' }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* Stepper */}
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((step, index) => (
                        <Step key={step.label}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* Step Content */}
                {renderStepContent(activeStep)}
            </DialogContent>

            {/* Navigation Buttons */}
            <DialogActions sx={{
                px: 3,
                py: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                justifyContent: 'space-between',
            }}>
                <Button onClick={onCancel} color="inherit">Cancel</Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {activeStep > 0 && (
                        <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
                            Back
                        </Button>
                    )}
                    {activeStep < steps.length - 1 ? (
                        <Button variant="contained" onClick={handleNext} endIcon={<ArrowForwardIcon />}>
                            Next
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={handleSubmit} disabled={loading.submit}
                            startIcon={loading.submit ? <CircularProgress size={20} /> : <CheckIcon />}>
                            {loading.submit ? 'Creating...' : 'Create Asset'}
                        </Button>
                    )}
                </Box>
            </DialogActions>

            <GeometryEditorModal
                open={isMapModalOpen}
                onClose={() => setMapModalOpen(false)}
                onSave={handleSaveGeometry}
                expectedGeometryType={expectedGeometryType}
            />
        </React.Fragment>
    );
};

export default CreateAssetForm;
